'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import NextLink from 'next/link';
import {
  usePathname,
  useRouter,
  useParams as useNextParams,
} from 'next/navigation';

type To = string;

type LinkProps = Omit<React.ComponentProps<typeof NextLink>, 'href'> & {
  to: To;
  replace?: boolean;
};

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  ({ to, replace, children, ...rest }, ref) => (
    <NextLink ref={ref} href={to as any} replace={replace} {...rest}>
      {children}
    </NextLink>
  ),
);
Link.displayName = 'Link';

type NavLinkClassName = string | ((state: { isActive: boolean; isPending: boolean }) => string);

type NavLinkProps = LinkProps & {
  className?: NavLinkClassName;
  end?: boolean;
  children?: React.ReactNode | ((state: { isActive: boolean; isPending: boolean }) => React.ReactNode);
};

export const NavLink = React.forwardRef<HTMLAnchorElement, NavLinkProps>(
  ({ to, replace, className, end, children, ...rest }, ref) => {
    const pathname = usePathname() ?? '/';
    const isActive = end ? pathname === to : pathname.startsWith(to);
    const computedClassName = typeof className === 'function'
      ? (className as ((state: { isActive: boolean; isPending: boolean }) => string))({ isActive, isPending: false })
      : className;

    return (
      <NextLink
        ref={ref}
        href={to as any}
        replace={replace}
        aria-current={isActive ? 'page' : undefined}
        className={computedClassName}
        {...rest}
      >
        {typeof children === 'function'
          ? (children as ((state: { isActive: boolean; isPending: boolean }) => React.ReactNode))({ isActive, isPending: false })
          : children}
      </NextLink>
    );
  },
);
NavLink.displayName = 'NavLink';

type NavigateProps = {
  to: To;
  replace?: boolean;
  state?: unknown;
};

export const Navigate: React.FC<NavigateProps> = ({ to, replace = false }) => {
  const router = useRouter();

  useEffect(() => {
    if (replace) {
      router.replace(to as any);
    } else {
      router.push(to as any);
    }
  }, [replace, router, to]);

  return null;
};

type NavigateFunction = (to: To | number, options?: { replace?: boolean; state?: unknown }) => void;

export const useNavigate = (): NavigateFunction => {
  const router = useRouter();

  return useCallback(
    (to, options) => {
      if (typeof to === 'number') {
        if (to === -1) {
          router.back();
        } else if (to === 1) {
          router.forward();
        } else if (typeof window !== 'undefined') {
          window.history.go(to);
        }
        return;
      }

      if (options?.replace) {
        router.replace(to as any);
      } else {
        router.push(to as any);
      }
    },
    [router],
  );
};

export const useLocation = () => {
  const pathname = usePathname() ?? '/';
  const [searchParams] = useSearchParams();
  const [hash, setHash] = useState('');

  useEffect(() => {
    const updateHash = () => {
      setHash(window.location.hash ?? '');
    };

    updateHash();
    window.addEventListener('hashchange', updateHash);

    return () => window.removeEventListener('hashchange', updateHash);
  }, []);

  const search = useMemo(() => {
    const value = searchParams.toString();
    return value ? `?${value}` : '';
  }, [searchParams]);

  return {
    pathname,
    search,
    hash,
    state: null,
    key: 'next',
  } as const;
};

type ParamsRecord = Record<string, string>;

export const useParams = <T extends ParamsRecord = ParamsRecord>(): T => {
  const params = useNextParams();

  return useMemo(() => {
    const record: ParamsRecord = {};

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        record[key] = Array.isArray(value) ? value[value.length - 1] : (value ?? '');
      });
    }

    return record as T;
  }, [params]);
};

type SearchParamsRecord = Record<string, string | number | boolean | readonly (string | number | boolean)[]>;
type SearchParamsInput = URLSearchParams | string | SearchParamsRecord;
type SetSearchParamsInput = SearchParamsInput | ((prev: URLSearchParams) => SearchParamsInput);

export const useSearchParams = (): [URLSearchParams, (nextInit: SetSearchParamsInput, options?: { replace?: boolean }) => void] => {
  const router = useRouter();
  const pathname = usePathname() ?? '/';
  const [search, setSearch] = useState<string>(() => {
    if (typeof window === 'undefined') {
      return '';
    }
    return window.location.search;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const handlePopState = () => {
      setSearch(window.location.search);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const setSearchParams = useCallback(
    (nextInit: SetSearchParamsInput, options?: { replace?: boolean }) => {
      const current = new URLSearchParams(search);

      const resolveInit = (init: SearchParamsInput): URLSearchParams => {
        if (init instanceof URLSearchParams) {
          return new URLSearchParams(init.toString());
        }

        if (typeof init === 'string') {
          return new URLSearchParams(init);
        }

        return new URLSearchParams(
          Object.entries(init).flatMap(([key, value]) => {
            if (Array.isArray(value)) {
              return value.map((item) => [key, String(item)]);
            }
            return [[key, String(value)]];
          }) as any,
        );
      };

      let nextParams: URLSearchParams;

      if (typeof nextInit === 'function') {
        const result = nextInit(current);
        nextParams = resolveInit(result);
      } else {
        nextParams = resolveInit(nextInit);
      }

      const searchString = nextParams.toString();
      const url = searchString ? `${pathname}?${searchString}` : pathname;

      if (options?.replace) {
        router.replace(url as any);
      } else {
        router.push(url as any);
      }

      setSearch(searchString ? `?${searchString}` : '');
    },
    [pathname, router, search],
  );

  return [useMemo(() => new URLSearchParams(search), [search]), setSearchParams];
};

export const Outlet: React.FC<{ children?: React.ReactNode }> = ({ children }) => <>{children}</>;

export const BrowserRouter: React.FC<{ children: React.ReactNode }> = ({ children }) => <>{children}</>;
export const MemoryRouter: React.FC<{ children: React.ReactNode }> = ({ children }) => <>{children}</>;

export const Routes: React.FC<{ children: React.ReactNode }> = ({ children }) => <>{children}</>;

export const Route: React.FC<{ children?: React.ReactNode }> = ({ children }) => <>{children}</>;
